import React, { useCallback, useMemo } from "react";
import { useFiltersContext } from "../../../pages/Workspace/UserFilters/FiltersContext";
import { ProductCategoryInput } from "../ProductCategory/ProductCategoryInput";
import { useInsideFilterChip } from "../../../pages/Workspace/UserFilters/FiltersChips/hooks/useInsideFilterChip";
import { FaiFilterSection } from "./FaiFilter/FaiFilterSection";
import { InstallAgeSection } from "./InstallAgeFilter/InstallAgeSection";
import { FilterLayout } from "./FilterLayout";
import CollectionSelect from "../../CollectionSelect/CollectionSelect";

export const ProductCategoriesFilter: React.FC = React.memo(() => {
  const { draftFilters, setDraftFilters } = useFiltersContext();
  const { insideChip, elementRef } = useInsideFilterChip();

  // Get initial selected items from draftFilters
  const selectedProductCategories = useMemo(
    () => draftFilters?.productCategories?.ids || [],
    [draftFilters?.productCategories?.ids]
  );

  // Get FAI selected items (store all selected IDs directly)
  const selectedFaiIds = useMemo(() => {
    return [
      ...(draftFilters?.productCategories?.departmentIds || []),
      ...(draftFilters?.productCategories?.roleIds || []),
    ];
  }, [
    draftFilters?.productCategories?.departmentIds,
    draftFilters?.productCategories?.roleIds,
  ]);

  const handleFaiSelect = useCallback(
    (selectedDepartmentIds: string[], selectedRoleIds: string[]) => {
      setDraftFilters((prevFilters) => ({
        ...prevFilters,
        productCategories: {
          ...prevFilters?.productCategories,
          ids: prevFilters?.productCategories?.ids || [],
          departmentIds:
            selectedDepartmentIds.length > 0
              ? selectedDepartmentIds
              : undefined,
          roleIds: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
        },
      }));
    },
    [setDraftFilters]
  );

  // Handle product category selection change
  const handleProductCategorySelect = useCallback(
    (productCategoryIds: string[]) => {
      if (productCategoryIds?.length === 0) {
        setDraftFilters((prevFilters) => ({
          ...prevFilters,
          productCategories: undefined,
        }));
        return;
      }

      setDraftFilters((prevFilters) => ({
        ...prevFilters,
        productCategories: {
          ...prevFilters?.productCategories,
          ids: productCategoryIds.length ? productCategoryIds : [],
        },
      }));
    },
    [setDraftFilters]
  );

  // Handle clearing the filter
  const handleClearFilter = useCallback(() => {
    setDraftFilters((prevFilters) => ({
      ...prevFilters,
      productCategories: undefined,
    }));
  }, [setDraftFilters]);

  const handleInstallAgeChange = useCallback(
    (installAge: { min?: number | null; max?: number | null }) => {
      setDraftFilters((prevFilters) => ({
        ...prevFilters,
        productCategories: {
          ...prevFilters?.productCategories,
          ids: prevFilters?.productCategories?.ids || [],
          installAgeMonths:
            installAge.min === undefined && installAge.max === undefined
              ? undefined
              : installAge,
        },
      }));
    },
    [setDraftFilters]
  );

  return (
    <FilterLayout
      title="Product Categories"
      onClear={handleClearFilter}
      showHeader={!insideChip}
      refProp={elementRef}
      id="product-categories-filter"
    >
      <ProductCategoryInput
        selectedItems={selectedProductCategories}
        onProductCategorySelect={handleProductCategorySelect}
        id="user-product-category-input"
      />

      <CollectionSelect
        placeholder="Search Collections"
        type="product_category"
        onSelect={({ ids }) =>
          handleProductCategorySelect([
            ...selectedProductCategories,
            ...(ids as string[]).filter(
              (id) => !selectedProductCategories.includes(id)
            ),
          ])
        }
      />

      <FaiFilterSection
        disabled={selectedProductCategories.length === 0}
        selectedIds={selectedFaiIds}
        onFaiSelect={handleFaiSelect}
        id="product-categories-fai-filter"
      />

      <InstallAgeSection
        filterHasSelection={selectedProductCategories.length > 0}
        disabled={selectedProductCategories.length === 0}
        value={
          draftFilters?.productCategories?.installAgeMonths || {
            min: undefined,
            max: undefined,
          }
        }
        onChange={handleInstallAgeChange}
        sx={{ mb: 1 }}
      />
    </FilterLayout>
  );
});
